// components/StructuredData.js - Structured data component for better SEO
import React from 'react';

// Organization structured data
export const OrganizationStructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Your Therapist",
    "description": "AI-driven terapi 24/7 kombinerat med legitimerade terapeuter",
    "url": "https://www.yourtherapist.se",
    "logo": "https://www.yourtherapist.se/logo512.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+46-XX-XXX-XX-XX",
      "contactType": "customer service",
      "availableLanguage": ["Swedish", "English"]
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SE",
      "addressLocality": "Stockholm"
    },
    "sameAs": [
      "https://www.facebook.com/yourtherapist",
      "https://www.instagram.com/yourtherapist"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

// Medical Business structured data
export const MedicalBusinessStructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "Your Therapist",
    "description": "AI-driven terapi 24/7 kombinerat med legitimerade terapeuter. Säker, tillgänglig och personlig mental hälsovård.",
    "url": "https://www.yourtherapist.se",
    "telephone": "+46-XX-XXX-XX-XX",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SE",
      "addressLocality": "Stockholm"
    },
    "openingHours": "Mo-Su 00:00-23:59",
    "medicalSpecialty": "Psychiatry",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Therapy Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI-driven Therapy",
            "description": "24/7 tillgänglig AI-terapi för mental hälsovård"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Licensed Therapist Consultation",
            "description": "Konsultation med legitimerade terapeuter"
          }
        }
      ]
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

// WebSite structured data with search action
export const WebSiteStructuredData = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Your Therapist",
    "url": "https://www.yourtherapist.se",
    "description": "AI-driven terapi 24/7 kombinerat med legitimerade terapeuter",
    "inLanguage": "sv-SE",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.yourtherapist.se/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

// FAQ structured data
export const FAQStructuredData = ({ faqs }) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

// Breadcrumb structured data
export const BreadcrumbStructuredData = ({ breadcrumbs }) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

// Blog Post structured data for SEO
export const BlogPostStructuredData = ({ posts }) => {
  if (!posts || posts.length === 0) return null;

  // For single post (article page)
  if (posts.length === 1) {
    const post = posts[0];
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt || post.meta_description || `Läs "${post.title}" på Your Therapist blog`,
      "image": post.featured_image_url || "https://www.yourtherapist.se/images/blog-social.jpg",
      "author": {
        "@type": "Person",
        "name": post.author_name || "Your Therapist Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Your Therapist",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.yourtherapist.se/logo512.png"
        }
      },
      "datePublished": post.published_at || post.created_at,
      "dateModified": post.updated_at || post.created_at,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://www.yourtherapist.se/blog/${post.slug}`
      },
      "url": `https://www.yourtherapist.se/blog/${post.slug}`,
      "wordCount": Math.ceil(post.reading_time_minutes * 200), // Approximate word count
      "timeRequired": `PT${post.reading_time_minutes}M`,
      "articleSection": post.categories?.[0]?.name || "AI Therapy",
      "keywords": post.meta_keywords || post.categories?.map(c => c.name).join(', ') || "AI-terapi, mental hälsa, psykologi"
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    );
  }

  // For blog listing (multiple posts)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Your Therapist Blog",
    "description": "Expertinsikter om AI-terapi, mental hälsa och psykologi från våra terapeuter och forskare",
    "url": "https://www.yourtherapist.se/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Your Therapist",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.yourtherapist.se/logo512.png"
      }
    },
    "blogPost": posts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt || `Läs "${post.title}" på Your Therapist blog`,
      "image": post.featured_image_url || "https://www.yourtherapist.se/images/blog-social.jpg",
      "author": {
        "@type": "Person",
        "name": post.author_name || "Your Therapist Team"
      },
      "datePublished": post.published_at || post.created_at,
      "url": `https://www.yourtherapist.se/blog/${post.slug}`,
      "wordCount": Math.ceil(post.reading_time_minutes * 200),
      "timeRequired": `PT${post.reading_time_minutes}M`,
      "articleSection": post.categories?.[0]?.name || "AI Therapy"
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};